import { useState, useEffect, useCallback } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ChangePasswordModal from '../components/common/ChangePasswordModal';
import { notificationApi, taskManagerNotificationApi } from '../api';
import greythrLogo from '../assets/greythr-logo.png';

const MODULES = [
  {
    key: 'materialwatch',
    name: 'MaterialWatch',
    description: 'Safety kit inventory, worker kits, stationery issuance and procurement management',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-10 h-10">
        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
      </svg>
    ),
    color: 'indigo',
  },
  {
    key: 'material_master',
    name: 'Material Master',
    description: 'Material & price master data, estimations and BOQ cost intelligence',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-10 h-10">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m7.5-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21" />
      </svg>
    ),
    color: 'amber',
  },
  {
    key: 'task_manager',
    name: 'Task Manager',
    description: 'Task, project, and job tracking across departments',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-10 h-10">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'emerald',
  },
];

const colorMap = {
  indigo: {
    bg: 'bg-indigo-50 hover:bg-indigo-100',
    icon: 'text-indigo-600',
    border: 'border-indigo-200 hover:border-indigo-400',
    btn: 'bg-indigo-600 hover:bg-indigo-700',
  },
  amber: {
    bg: 'bg-amber-50 hover:bg-amber-100',
    icon: 'text-amber-600',
    border: 'border-amber-200 hover:border-amber-400',
    btn: 'bg-amber-600 hover:bg-amber-700',
  },
  emerald: {
    bg: 'bg-emerald-50 hover:bg-emerald-100',
    icon: 'text-emerald-600',
    border: 'border-emerald-200 hover:border-emerald-400',
    btn: 'bg-emerald-600 hover:bg-emerald-700',
  },
  violet: {
    bg: 'bg-violet-50 hover:bg-violet-100',
    icon: 'text-violet-600',
    border: 'border-violet-200 hover:border-violet-400',
    btn: 'bg-violet-600 hover:bg-violet-700',
  },
};

// Greythr is a third-party HR/payroll tool, not a module hosted by this
// platform — no permission gating, visible to every logged-in user, and
// opened in a new tab (rel=noopener since it's an external, unrelated
// origin) rather than navigated to in place like the real modules above.
const GREYTHR_URL = 'https://sobha-projects.greythr.com';

// Tile order is a personal layout preference, not shared/permission state —
// kept in localStorage per user (so a shared machine with multiple logins
// doesn't mix up whose arrangement is whose) rather than the backend.
function useTileOrder(storageId, defaultOrder) {
  const storageKey = `portal_tile_order_${storageId || 'anon'}`;
  const [order, setOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (Array.isArray(saved)) return saved;
    } catch { /* ignore malformed storage */ }
    return defaultOrder;
  });

  // If the saved order is missing a tile the user has since gained (or is
  // hanging onto one they lost) access to, reconcile against the current
  // default set — new tiles appended at the end, stale keys dropped.
  useEffect(() => {
    setOrder((prev) => {
      const known = prev.filter((k) => defaultOrder.includes(k));
      const missing = defaultOrder.filter((k) => !known.includes(k));
      const next = [...known, ...missing];
      return next.length === prev.length && next.every((k, i) => k === prev[i]) ? prev : next;
    });
  }, [defaultOrder.join(',')]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(order));
  }, [order, storageKey]);

  return [order, setOrder];
}

export default function Portal() {
  const { user, hasModuleAccess, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [showChangePw, setShowChangePw] = useState(false);

  const accessibleModules = MODULES.filter((m) => hasModuleAccess(m.key));
  const defaultTileKeys = [...accessibleModules.map((m) => m.key), 'greythr'];
  const [tileOrder, setTileOrder] = useTileOrder(user?.username || user?.employeeId, defaultTileKeys);
  const [dragKey, setDragKey] = useState(null);

  // Reorders live as the dragged tile passes OVER another one (on dragover,
  // not drop) so the rest of the row visibly shifts to make room while
  // you're still dragging — the classic "make room" reorder feel, instead
  // of nothing moving until you let go.
  const reorder = (targetKey) => {
    if (!dragKey || dragKey === targetKey) return;
    setTileOrder((prev) => {
      const from = prev.indexOf(dragKey);
      const to = prev.indexOf(targetKey);
      if (from === -1 || to === -1 || from === to) return prev;
      const next = [...prev];
      next.splice(from, 1);
      next.splice(to, 0, dragKey);
      return next;
    });
  };

  // dragend fires no matter how the drag finishes — dropped on a valid
  // target, dropped outside the grid, or cancelled with Escape — so this is
  // the one place that reliably clears the "being dragged" dim/opacity
  // state. Relying on onDrop alone left the last-dragged tile permanently
  // greyed out whenever a drag ended any other way.
  const clearDrag = () => setDragKey(null);

  // Per-module notification badges shown right on the launcher tile — the
  // whole point being that new activity in a module is visible from Portal
  // itself, before ever opening it. Each module's count comes from that
  // module's own backend (client-side poll, same pattern this app already
  // uses for MaterialWatch's own notification bell) — only polled when the
  // user actually has access to that module.
  const [moduleBadges, setModuleBadges] = useState({});
  const canSeeMaterialwatch = hasModuleAccess('materialwatch');
  const canSeeTaskManager = hasModuleAccess('task_manager');
  const refreshBadges = useCallback(async () => {
    if (canSeeMaterialwatch) {
      notificationApi.unreadCount()
        .then((res) => setModuleBadges((prev) => ({ ...prev, materialwatch: res.data.data.count })))
        .catch(() => {});
    }
    if (canSeeTaskManager) {
      taskManagerNotificationApi.summary()
        .then((res) => setModuleBadges((prev) => ({ ...prev, task_manager: res.data.total })))
        .catch(() => {});
    }
  }, [canSeeMaterialwatch, canSeeTaskManager]);
  useEffect(() => {
    if (!canSeeMaterialwatch && !canSeeTaskManager) return;
    refreshBadges();
    const interval = setInterval(refreshBadges, 15000);
    return () => clearInterval(interval);
  }, [canSeeMaterialwatch, canSeeTaskManager, refreshBadges]);

  const handleOpen = (mod) => {
    if (mod.key === 'materialwatch') {
      const destPath = user?.siteId ? '/inventory' : '/dashboard';
      // A dedicated MaterialWatch URL means this is a portal-only build (separate
      // subdomain/container) — that module isn't compiled into this app at all,
      // so it's a real cross-origin redirect, not client-side navigation.
      if (process.env.REACT_APP_MATERIALWATCH_URL) {
        window.location.href = `${process.env.REACT_APP_MATERIALWATCH_URL}${destPath}`;
      } else {
        navigate(destPath);
      }
    } else if (mod.key === 'material_master') {
      // Material Master is always its own separate app/build — not part of
      // this shared codebase at all — so this is always a real cross-origin
      // redirect, never client-side navigation.
      if (process.env.REACT_APP_MATERIAL_MASTER_URL) {
        window.location.href = process.env.REACT_APP_MATERIAL_MASTER_URL;
      }
    } else if (mod.key === 'task_manager') {
      // Task Manager is its own separate app/build, same shape as Material
      // Master — always a real cross-origin redirect.
      if (process.env.REACT_APP_TASK_MANAGER_URL) {
        window.location.href = process.env.REACT_APP_TASK_MANAGER_URL;
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-white">
              <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </div>
          <span className="text-lg font-bold text-gray-900">Platform</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-right">
            <p className="font-medium text-gray-900">{user?.fullName || user?.username}</p>
            {user?.employeeId && <p className="text-xs text-gray-500">{user.employeeId}</p>}
          </div>
          <button
            onClick={() => setShowChangePw(true)}
            className="flex items-center gap-1.5 text-sm font-medium text-indigo-600 border border-indigo-200 bg-indigo-50 rounded-lg px-3 py-1.5 hover:bg-indigo-100 hover:border-indigo-300 transition-colors"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
            </svg>
            Change Password
          </button>
          <button
            onClick={() => { logout(); window.location.href = '/login'; }}
            className="text-sm text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
          >
            Sign out
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 px-6 py-10 max-w-5xl mx-auto w-full">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome{user?.fullName ? `, ${user.fullName.split(' ')[0]}` : ''}
          </h1>
          <p className="text-gray-500 mt-1">Select a module to get started</p>
        </div>

        {/* Admin quick actions */}
        {isAdmin && (
          <div className="mb-8 flex flex-wrap gap-3">
            <NavLink
              to="/users"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm transition-colors"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-500">
                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
              </svg>
              Manage Users
            </NavLink>
          </div>
        )}

        {accessibleModules.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-8 h-8 text-gray-400">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
              </svg>
            </div>
            <p className="text-gray-600 font-medium">No modules assigned</p>
            <p className="text-gray-400 text-sm mt-1">Contact your administrator to get access</p>
          </div>
        )}

        {accessibleModules.length > 0 && (
          <p className="text-xs text-gray-400 mb-3 -mt-1">Drag a tile to rearrange — your order is remembered.</p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {tileOrder.map((key) => {
            if (key === 'greythr') {
              const c = colorMap.violet;
              return (
                <a
                  key="greythr"
                  href={GREYTHR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  draggable
                  onDragStart={() => setDragKey('greythr')}
                  onDragOver={(e) => { e.preventDefault(); reorder('greythr'); }}
                  onDrop={(e) => e.preventDefault()}
                  onDragEnd={clearDrag}
                  className={`relative text-left rounded-xl border-2 p-6 transition-all duration-150 cursor-grab active:cursor-grabbing ${c.bg} ${c.border} shadow-sm hover:shadow-md group ${dragKey === 'greythr' ? 'opacity-50' : ''}`}
                >
                  <div className="mb-4">
                    <img src={greythrLogo} alt="Greythr" className="h-20 w-auto" />
                  </div>
                  <p className="text-sm text-gray-500 mt-1 leading-relaxed">HR, payroll, and attendance management</p>
                  <div className={`mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-white px-4 py-2 rounded-lg ${c.btn} transition-colors`}>
                    Open
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                      <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                    </svg>
                  </div>
                </a>
              );
            }

            const mod = accessibleModules.find((m) => m.key === key);
            if (!mod) return null; // access revoked since order was saved
            const c = colorMap[mod.color] || colorMap.indigo;
            const badge = moduleBadges[mod.key];
            return (
              <button
                key={mod.key}
                onClick={() => handleOpen(mod)}
                draggable
                onDragStart={() => setDragKey(mod.key)}
                onDragOver={(e) => { e.preventDefault(); reorder(mod.key); }}
                onDrop={(e) => e.preventDefault()}
                onDragEnd={clearDrag}
                className={`relative text-left rounded-xl border-2 p-6 transition-all duration-150 cursor-grab active:cursor-grabbing ${c.bg} ${c.border} shadow-sm hover:shadow-md group ${dragKey === mod.key ? 'opacity-50' : ''}`}
              >
                {!!badge && (
                  <span className="absolute top-3 right-3 inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold leading-none shadow-sm">
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
                <div className={`mb-4 ${c.icon}`}>{mod.icon}</div>
                <h2 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-700 transition-colors">
                  {mod.name}
                </h2>
                <p className="text-sm text-gray-500 mt-1 leading-relaxed">{mod.description}</p>
                <div className={`mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-white px-4 py-2 rounded-lg ${c.btn} transition-colors`}>
                  Open
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                  </svg>
                </div>
              </button>
            );
          })}
        </div>
      </main>

      <ChangePasswordModal show={showChangePw} onClose={() => setShowChangePw(false)} />
    </div>
  );
}
